import EditSquare from "@mui/icons-material/EditSquare";
import PrivacyTipOutlined from "@mui/icons-material/PrivacyTipOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ScoutIcon from "components/ScoutIcon";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ScoutConversationSettingsMenu } from "./ScoutConversationSettingsMenu";
import { SearchThreadsDialog } from "./Search";

export function ScoutToolbar({
  conversationTitle,
  showLogo,
  showTitle,
  getSearchDialogContainer,
  allowMutations = false,
  onAllowMutationsChange,
  settingsDisabled = false,
  hasScoutProAccess = true,
  onTogglePrivacyPanel,
}: {
  conversationTitle?: string;
  showLogo: boolean;
  showTitle: boolean;
  getSearchDialogContainer?: () => Element | null;
  allowMutations?: boolean;
  onAllowMutationsChange?: (next: boolean) => void;
  settingsDisabled?: boolean;
  hasScoutProAccess?: boolean;
  onTogglePrivacyPanel?: () => void;
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { tenantId, conversationId } = useParams();
  const [searchOpen, setSearchOpen] = useState(false);
  const title = conversationTitle?.trim();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Stack
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 1,
        pointerEvents: "auto",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{
          minWidth: 0,
          flex: 1,
          pr: 2,
        }}
      >
        <Box
          sx={{
            padding: 1,
            width: theme.spacing(5),
            height: theme.spacing(5),
            flexShrink: 0,
          }}
        >
          {showLogo && <ScoutIcon sx={{ color: theme.palette.encampBlue.main }} />}
        </Box>
        {showTitle && title && (
          <Typography
            variant="h6"
            color="text.secondary"
            noWrap
            sx={{
              fontSize: { xs: "1rem", sm: "1.125rem" },
              fontWeight: 400,
              lineHeight: 1.4,
              transform: "translate(-6px, 1px)",
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
        )}
        {showTitle && !title && (
          <Skeleton
            variant="text"
            width={220}
            sx={{
              fontSize: { xs: "1rem", sm: "1.125rem" },
              lineHeight: 1.4,
            }}
          />
        )}
      </Stack>

      <Stack direction="row" alignItems="center" flexShrink={0}>
        {isMediumScreen ? (
          <Tooltip title="Privacy & Data">
            <IconButton
              color="primary"
              onClick={onTogglePrivacyPanel}
              aria-label="Privacy and Data"
            >
              <PrivacyTipOutlined />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            size="small"
            color="primary"
            startIcon={<PrivacyTipOutlined fontSize="small" />}
            onClick={onTogglePrivacyPanel}
          >
            Privacy & Data
          </Button>
        )}

        {conversationId && (
          <Tooltip title="New conversation">
            <IconButton onClick={() => navigate(`/o/${tenantId}/scout`)}>
              <EditSquare />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Search threads">
          <IconButton onClick={() => setSearchOpen(true)}>
            <SearchIcon />
          </IconButton>
        </Tooltip>

        <ScoutConversationSettingsMenu
          allowMutations={allowMutations}
          onAllowMutationsChange={onAllowMutationsChange ?? (() => undefined)}
          disabled={settingsDisabled}
          hasScoutProAccess={hasScoutProAccess}
        />
      </Stack>

      <SearchThreadsDialog
        key={String(searchOpen)}
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        dialogContainer={getSearchDialogContainer}
        onSelectThread={(thread) => {
          setSearchOpen(false);
          navigate(`/o/${tenantId}/scout/${thread.id}`);
        }}
      />
    </Stack>
  );
}
